import React, { useEffect, useState, useCallback, useRef } from 'react';

const drawerStyle = document.createElement('style');
drawerStyle.textContent = `@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`;
if (!document.head.querySelector('[data-drawer]')) { drawerStyle.setAttribute('data-drawer','1'); document.head.appendChild(drawerStyle); }
import { useNavigate } from 'react-router-dom';
import { campaignAPI, departmentAPI, employeeAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const TYPE_CONFIG = {
  recruitment:   { label: 'Tuyển dụng',    color: '#2563eb', bg: '#dbeafe' },
  training:      { label: 'Đào tạo',        color: '#7c3aed', bg: '#ede9fe' },
  engagement:    { label: 'Gắn kết',        color: '#059669', bg: '#d1fae5' },
  communication: { label: 'Truyền thông',   color: '#d97706', bg: '#fef3c7' },
  other:         { label: 'Khác',           color: '#64748b', bg: '#f1f5f9' },
};

const STATUS_CONFIG = {
  planning:  { label: 'Lên kế hoạch', color: '#64748b', bg: '#f1f5f9' },
  active:    { label: 'Đang triển khai', color: '#0891b2', bg: '#cffafe' },
  completed: { label: 'Hoàn thành',    color: '#16a34a', bg: '#dcfce7' },
  cancelled: { label: 'Đã huỷ',        color: '#dc2626', bg: '#fee2e2' },
};

const fmt = (n) => Number(n || 0).toLocaleString('vi-VN');

function slugify(text) {
  return (text || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[đĐ]/g, m => m === 'đ' ? 'd' : 'D')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

const Badge = ({ value, config }) => {
  const c = config[value] || config.other || config.planning;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 600,
      color: c.color,
      background: c.bg,
    }}>{c.label}</span>
  );
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('vi-VN') : '—';

const EMPTY = {
  title: '', type: 'recruitment', description: '', objectives: '', results: '',
  department_id: '', status: 'planning', start_date: '', end_date: '',
  budget: '', target_count: '', owner_id: '',
};

export default function CampaignsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = ['admin', 'hr_manager'].includes(user?.role);
  const canDelete = user?.role === 'admin';

  const [campaigns, setCampaigns] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [filterDept, setFilterDept] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [modal, setModal] = useState(null); // null | 'create' | 'edit' | 'view'
  const [form, setForm] = useState(EMPTY);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterDept) params.department_id = filterDept;
      if (filterType) params.type = filterType;
      if (filterStatus) params.status = filterStatus;
      const [cRes, dRes, eRes] = await Promise.all([
        campaignAPI.list(params),
        departmentAPI.list(),
        employeeAPI.list({ limit: 200 }),
      ]);
      setCampaigns(cRes.data.data);
      setDepartments(dRes.data.data);
      setEmployees(eRes.data.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Không thể tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, [filterDept, filterType, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY); setFormError(''); setModal('create'); };
  const openEdit = (c) => {
    setForm({
      title: c.title || '',
      type: c.type || 'other',
      description: c.description || '',
      objectives: c.objectives || '',
      results: c.results || '',
      department_id: c.department_id || '',
      status: c.status || 'planning',
      start_date: c.start_date ? c.start_date.slice(0, 10) : '',
      end_date: c.end_date ? c.end_date.slice(0, 10) : '',
      budget: c.budget || '',
      target_count: c.target_count || '',
      owner_id: c.owner_id || '',
    });
    setSelected(c);
    setFormError('');
    setModal('edit');
  };
  const openView = (c) => { setSelected(c); setModal('view'); };

  const handleSubmit = async () => {
    if (!form.title.trim()) { setFormError('Tên chiến dịch không được để trống'); return; }
    if (form.start_date && form.end_date && form.start_date > form.end_date) {
      setFormError('Ngày bắt đầu phải trước ngày kết thúc');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      const payload = {
        ...form,
        department_id: form.department_id || null,
        owner_id: form.owner_id || null,
        budget: form.budget || 0,
        target_count: form.target_count || 0,
      };
      if (modal === 'create') await campaignAPI.create(payload);
      else await campaignAPI.update(selected.id, payload);
      setModal(null);
      load();
    } catch (e) {
      setFormError(e.response?.data?.message || 'Lỗi lưu dữ liệu');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn có chắc muốn xoá chiến dịch này?')) return;
    try {
      await campaignAPI.delete(id);
      load();
    } catch (e) {
      setError(e.response?.data?.message || 'Không thể xoá');
    }
  };

  // Stats
  const total = campaigns.length;
  const active = campaigns.filter(c => c.status === 'active').length;
  const planning = campaigns.filter(c => c.status === 'planning').length;
  const completed = campaigns.filter(c => c.status === 'completed').length;

  const card = (label, value, color) => (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '18px 24px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.07)', flex: 1,
      borderLeft: `4px solid ${color}`,
    }}>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{label}</div>
    </div>
  );

  const inputStyle = {
    width: '100%', padding: '8px 12px', borderRadius: 7,
    border: '1px solid #e2e8f0', fontSize: 14, boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', margin: 0 }}>Chiến dịch</h1>
        <p style={{ fontSize: 14, color: '#64748b', margin: '4px 0 0' }}>Quản lý các chiến dịch tuyển dụng, đào tạo và gắn kết nhân viên</p>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {card('Tổng chiến dịch', total, '#6366f1')}
        {card('Đang triển khai', active, '#0891b2')}
        {card('Lên kế hoạch', planning, '#64748b')}
        {card('Hoàn thành', completed, '#16a34a')}
      </div>

      {/* Filters + action */}
      <div style={{
        background: '#fff', borderRadius: 12, padding: 20,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 20,
      }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={filterDept} onChange={e => setFilterDept(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 180 }}>
            <option value="">Tất cả phòng ban</option>
            <option value="none">Toàn công ty</option>
            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 160 }}>
            <option value="">Tất cả loại</option>
            {Object.entries(TYPE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inputStyle, width: 'auto', minWidth: 180 }}>
            <option value="">Tất cả trạng thái</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <div style={{ marginLeft: 'auto' }}>
            {canEdit && (
              <button onClick={openCreate} style={{
                background: 'linear-gradient(135deg,#6366f1,#4f46e5)',
                color: '#fff', border: 'none', borderRadius: 8,
                padding: '9px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14,
              }}>+ Tạo chiến dịch</button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 16px', borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Campaign cards grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Đang tải...</div>
      ) : campaigns.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎯</div>
          <div style={{ fontSize: 15 }}>Chưa có chiến dịch nào</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {campaigns.map(c => <CampaignCard key={c.id} c={c} canEdit={canEdit} canDelete={canDelete} onView={() => navigate(`/campaigns/${c.id}/${slugify(c.title)}`)} onEdit={openEdit} onDelete={handleDelete} />)}
        </div>
      )}

      {/* Modal */}
      {(modal === 'create' || modal === 'edit') && (
        <Modal title={modal === 'create' ? 'Tạo chiến dịch mới' : 'Chỉnh sửa chiến dịch'} onClose={() => setModal(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={labelStyle}>Tên chiến dịch *</label>
              <input style={inputStyle} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="VD: Tuyển dụng IT Q3/2025" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Loại chiến dịch</label>
                <select style={inputStyle} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                  {Object.entries(TYPE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Trạng thái</label>
                <select style={inputStyle} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label style={labelStyle}>Phòng ban phụ trách</label>
              <select style={inputStyle} value={form.department_id} onChange={e => setForm(f => ({ ...f, department_id: e.target.value }))}>
                <option value="">Toàn công ty</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Người phụ trách</label>
              <select style={inputStyle} value={form.owner_id} onChange={e => setForm(f => ({ ...f, owner_id: e.target.value }))}>
                <option value="">— Chọn nhân viên —</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.first_name} {e.last_name} ({e.employee_code})</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Ngày bắt đầu</label>
                <input type="date" style={inputStyle} value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
              </div>
              <div>
                <label style={labelStyle}>Ngày kết thúc</label>
                <input type="date" style={inputStyle} value={form.end_date} min={form.start_date || undefined} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Ngân sách (VNĐ)</label>
                <input type="number" style={inputStyle} value={form.budget} onChange={e => setForm(f => ({ ...f, budget: e.target.value }))} placeholder="0" min={0} />
                {form.budget > 0 && <div style={{ fontSize: 11, color: '#6366f1', marginTop: 3 }}>{fmt(form.budget)} đ</div>}
              </div>
              <div>
                <label style={labelStyle}>Số lượng mục tiêu</label>
                <input type="number" style={inputStyle} value={form.target_count} onChange={e => setForm(f => ({ ...f, target_count: e.target.value }))} placeholder="0" min={0} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Mô tả</label>
              <textarea style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Tóm tắt chiến dịch..." />
            </div>
            <div>
              <label style={labelStyle}>🎯 Mục tiêu chiến dịch</label>
              <textarea style={{ ...inputStyle, minHeight: 90, resize: 'vertical' }} value={form.objectives} onChange={e => setForm(f => ({ ...f, objectives: e.target.value }))} placeholder="Mô tả mục tiêu cụ thể cần đạt được&#10;VD: Tuyển 5 kỹ sư backend Senior&#10;Tổ chức 3 buổi đào tạo nội bộ..." />
            </div>
            <div>
              <label style={labelStyle}>📊 Kết quả thực tế</label>
              <textarea style={{ ...inputStyle, minHeight: 90, resize: 'vertical' }} value={form.results} onChange={e => setForm(f => ({ ...f, results: e.target.value }))} placeholder="Ghi nhận kết quả đạt được sau khi triển khai&#10;VD: Đã tuyển 4/5 vị trí, đạt 80% mục tiêu..." />
            </div>
            {formError && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '8px 12px', borderRadius: 6, fontSize: 13 }}>{formError}</div>}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
              <button onClick={() => setModal(null)} style={{ padding: '8px 20px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: 14 }}>Huỷ</button>
              <button onClick={handleSubmit} disabled={submitting} style={{
                padding: '8px 20px', borderRadius: 7, border: 'none',
                background: submitting ? '#a5b4fc' : 'linear-gradient(135deg,#6366f1,#4f46e5)',
                color: '#fff', cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 14,
              }}>{submitting ? 'Đang lưu...' : 'Lưu'}</button>
            </div>
          </div>
        </Modal>
      )}

    </div>
  );
}

function CampaignCard({ c, canEdit, canDelete, onView, onEdit, onDelete }) {
  const typeCfg = TYPE_CONFIG[c.type] || TYPE_CONFIG.other;
  const [hovered, setHovered] = useState(false);
  const daysLeft = c.end_date ? Math.ceil((new Date(c.end_date) - new Date()) / 86400000) : null;

  return (
    <div
      style={{
        background: '#fff', borderRadius: 12, overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        borderTop: `4px solid ${typeCfg.color}`,
        boxShadow: hovered ? '0 6px 20px rgba(0,0,0,0.12)' : '0 1px 4px rgba(0,0,0,0.07)',
        transform: hovered ? 'translateY(-2px)' : 'none',
        transition: 'box-shadow 0.15s, transform 0.15s',
        cursor: 'pointer',
      }}
      onClick={() => onView(c)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={{ padding: '18px 20px 14px', flex: 1 }}>
        {/* Badges */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
          <Badge value={c.type} config={TYPE_CONFIG} />
          <Badge value={c.status} config={STATUS_CONFIG} />
        </div>

        <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>{c.title}</div>

        {c.description && (
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12, lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {c.description}
          </div>
        )}

        {/* Info rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {c.department_name
            ? <InfoRow icon="🏬" label={c.department_name} />
            : <InfoRow icon="🌐" label="Toàn công ty" />}
          {c.owner_name && <InfoRow icon="👤" label={c.owner_name} />}
          {(c.start_date || c.end_date) && (
            <InfoRow icon="📅" label={`${fmtDate(c.start_date)} → ${fmtDate(c.end_date)}`} />
          )}
          {c.budget > 0 && <InfoRow icon="💰" label={`${fmt(c.budget)} đ`} />}
          {c.target_count > 0 && <InfoRow icon="🎯" label={`${c.target_count} người`} />}
        </div>

        {/* Objectives/results preview chips */}
        {(c.objectives || c.results) && (
          <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            {c.objectives && (
              <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                background: `${typeCfg.color}12`, color: typeCfg.color }}>
                🎯 Có mục tiêu
              </span>
            )}
            {c.results && (
              <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                background: '#dcfce7', color: '#16a34a' }}>
                📊 Có kết quả
              </span>
            )}
          </div>
        )}

        {/* Days left */}
        {c.status === 'active' && daysLeft !== null && (
          <div style={{
            marginTop: 10, padding: '4px 10px', borderRadius: 6, display: 'inline-block',
            background: daysLeft < 0 ? '#fee2e2' : daysLeft <= 7 ? '#fef3c7' : '#f0fdf4',
            color: daysLeft < 0 ? '#dc2626' : daysLeft <= 7 ? '#d97706' : '#16a34a',
            fontSize: 11, fontWeight: 600,
          }}>
            {daysLeft < 0 ? `Quá hạn ${Math.abs(daysLeft)} ngày` : daysLeft === 0 ? 'Kết thúc hôm nay' : `Còn ${daysLeft} ngày`}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 8, alignItems: 'center' }}>
        {/* View hint — always visible */}
        <span style={{ fontSize: 11, color: '#94a3b8', flex: 1 }}>Nhấn để xem chi tiết →</span>

        {canEdit && (
          <button onClick={e => { e.stopPropagation(); onEdit(c); }} style={{
            padding: '5px 12px', borderRadius: 6, border: '1px solid #e2e8f0',
            background: '#fff', cursor: 'pointer', fontSize: 12, color: '#475569', fontWeight: 500,
          }}>Sửa</button>
        )}
        {canDelete && (
          <button onClick={e => { e.stopPropagation(); onDelete(c.id); }} style={{
            padding: '5px 12px', borderRadius: 6, border: '1px solid #fca5a5',
            background: '#fff', cursor: 'pointer', fontSize: 12, color: '#dc2626', fontWeight: 500,
          }}>Xoá</button>
        )}
      </div>
    </div>
  );
}

function InfoRow({ icon, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
      <span style={{ fontSize: 13 }}>{icon}</span>
      <span>{label}</span>
    </div>
  );
}

function CampaignDrawer({ c, canEdit, onClose, onEdit }) {
  const [tab, setTab] = useState('overview');
  const typeCfg   = TYPE_CONFIG[c.type]   || TYPE_CONFIG.other;
  const statusCfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.planning;
  const daysLeft  = c.end_date ? Math.ceil((new Date(c.end_date) - new Date()) / 86400000) : null;

  const tabs = [
    { key: 'overview',    label: '📋 Tổng quan' },
    { key: 'objectives',  label: '🎯 Mục tiêu' },
    { key: 'results',     label: '📊 Kết quả' },
  ];

  // Render text with line breaks as bullet-style lines
  const renderLines = (text, emptyMsg) => {
    if (!text?.trim()) return (
      <div style={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic', padding: '12px 0' }}>{emptyMsg}</div>
    );
    return text.split('\n').filter(l => l.trim()).map((line, i) => (
      <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8 }}>
        <span style={{ color: typeCfg.color, fontWeight: 700, flexShrink: 0, marginTop: 2 }}>•</span>
        <span style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>{line.replace(/^[-•*]\s*/, '')}</span>
      </div>
    ));
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        width: '100%', maxWidth: 560, background: '#fff',
        display: 'flex', flexDirection: 'column',
        boxShadow: '-8px 0 32px rgba(0,0,0,0.18)',
        animation: 'slideIn 0.2s ease',
      }}>
        {/* Header */}
        <div style={{ borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
          {/* Color strip */}
          <div style={{ height: 4, background: `linear-gradient(90deg, ${typeCfg.color}, ${typeCfg.color}88)` }} />
          <div style={{ padding: '16px 20px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <Badge value={c.type} config={TYPE_CONFIG} />
                <Badge value={c.status} config={STATUS_CONFIG} />
                {c.status === 'active' && daysLeft !== null && (
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                    color: daysLeft < 0 ? '#dc2626' : daysLeft <= 7 ? '#d97706' : '#16a34a',
                    background: daysLeft < 0 ? '#fee2e2' : daysLeft <= 7 ? '#fef3c7' : '#dcfce7',
                  }}>
                    {daysLeft < 0 ? `Quá hạn ${Math.abs(daysLeft)}n` : daysLeft === 0 ? 'Hôm nay' : `Còn ${daysLeft}n`}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {canEdit && (
                  <button onClick={onEdit} style={{
                    padding: '5px 14px', borderRadius: 6, border: '1px solid #e2e8f0',
                    background: '#fff', cursor: 'pointer', fontSize: 12, color: '#475569', fontWeight: 500,
                  }}>✏️ Sửa</button>
                )}
                <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#94a3b8', lineHeight: 1, padding: 0 }}>×</button>
              </div>
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: '0 0 14px', lineHeight: 1.3 }}>{c.title}</h2>

            {/* Tab bar */}
            <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #f1f5f9', marginBottom: -1 }}>
              {tabs.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)} style={{
                  padding: '8px 16px', background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: tab === t.key ? 700 : 400,
                  color: tab === t.key ? typeCfg.color : '#64748b',
                  borderBottom: `2px solid ${tab === t.key ? typeCfg.color : 'transparent'}`,
                  marginBottom: -2, transition: 'all 0.15s', whiteSpace: 'nowrap',
                }}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 20px 28px' }}>

          {/* ── TAB: Tổng quan ── */}
          {tab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {c.description && (
                <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px', fontSize: 14, color: '#475569', lineHeight: 1.7 }}>
                  {c.description}
                </div>
              )}

              {/* Info grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <DetailItem label="Phòng ban" value={c.department_name || 'Toàn công ty'} icon="🏬" />
                <DetailItem label="Người phụ trách" value={c.owner_name || '—'} icon="👤" />
                <DetailItem label="Ngày bắt đầu" value={fmtDate(c.start_date)} icon="📅" />
                <DetailItem label="Ngày kết thúc" value={fmtDate(c.end_date)} icon="🏁" />
                <DetailItem label="Ngân sách" value={c.budget > 0 ? `${fmt(c.budget)} đ` : '—'} icon="💰" />
                <DetailItem label="Số lượng mục tiêu" value={c.target_count > 0 ? `${c.target_count} người` : '—'} icon="🎯" />
                <DetailItem label="Tạo bởi" value={c.created_by_name || '—'} icon="✍️" />
                <DetailItem label="Ngày tạo" value={c.created_at ? new Date(c.created_at).toLocaleDateString('vi-VN') : '—'} icon="🗓️" />
              </div>

              {/* Quick summary of objectives/results */}
              {(c.objectives || c.results) && (
                <div style={{ display: 'flex', gap: 10 }}>
                  {c.objectives && (
                    <div onClick={() => setTab('objectives')} style={{
                      flex: 1, background: `${typeCfg.color}0a`, border: `1px solid ${typeCfg.color}22`,
                      borderRadius: 10, padding: '12px 14px', cursor: 'pointer',
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: typeCfg.color, marginBottom: 4 }}>🎯 Mục tiêu</div>
                      <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5,
                        display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {c.objectives}
                      </div>
                    </div>
                  )}
                  {c.results && (
                    <div onClick={() => setTab('results')} style={{
                      flex: 1, background: '#f0fdf4', border: '1px solid #bbf7d0',
                      borderRadius: 10, padding: '12px 14px', cursor: 'pointer',
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#16a34a', marginBottom: 4 }}>📊 Kết quả</div>
                      <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5,
                        display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {c.results}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── TAB: Mục tiêu ── */}
          {tab === 'objectives' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: `${typeCfg.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🎯</div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>Mục tiêu chiến dịch</div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>Những gì cần đạt được khi kết thúc</div>
                </div>
              </div>

              {c.target_count > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#f8fafc', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
                  <span style={{ fontSize: 20 }}>👥</span>
                  <span style={{ fontSize: 13, color: '#475569' }}>Số lượng mục tiêu: </span>
                  <strong style={{ fontSize: 15, color: typeCfg.color }}>{c.target_count} người</strong>
                </div>
              )}

              <div style={{ background: '#fafafa', borderRadius: 10, padding: '16px', borderLeft: `3px solid ${typeCfg.color}` }}>
                {renderLines(c.objectives, 'Chưa có mục tiêu nào được đặt ra. Nhấn "Sửa" để thêm mục tiêu.')}
              </div>
            </div>
          )}

          {/* ── TAB: Kết quả ── */}
          {tab === 'results' && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>📊</div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>Kết quả thực tế</div>
                  <div style={{ fontSize: 12, color: '#94a3b8' }}>Những gì đã đạt được sau triển khai</div>
                </div>
              </div>

              {/* Status badge */}
              {c.status !== 'completed' && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '9px 14px', marginBottom: 14, fontSize: 13, color: '#92400e', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>⏳</span>
                  Chiến dịch chưa hoàn thành — kết quả có thể được cập nhật trong quá trình thực hiện.
                </div>
              )}
              {c.status === 'completed' && (
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '9px 14px', marginBottom: 14, fontSize: 13, color: '#15803d', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>✅</span>
                  Chiến dịch đã hoàn thành.
                </div>
              )}

              <div style={{ background: '#fafafa', borderRadius: 10, padding: '16px', borderLeft: '3px solid #22c55e' }}>
                {renderLines(c.results, 'Chưa có kết quả nào được ghi nhận. Nhấn "Sửa" để cập nhật kết quả.')}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailItem({ label, value, icon }) {
  return (
    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
        {icon && <span>{icon}</span>}{label}
      </div>
      <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 600 }}>{value}</div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#fff', borderRadius: 14, width: '100%', maxWidth: 580,
        maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <div style={{
          padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff',
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8' }}>×</button>
        </div>
        <div style={{ padding: '20px 24px 24px' }}>{children}</div>
      </div>
    </div>
  );
}
